import { GoogleGenAI, Type, Modality } from "@google/genai";
import AWS from "aws-sdk";
import type { Scene, CharacterImage } from "./Types";

AWS.config.update({
  region: process.env.REACT_APP_AWS_REGION || "us-east-1",
  credentials: new AWS.CognitoIdentityCredentials({
    IdentityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID || "",
  }),
});

const s3 = new AWS.S3();

if (!process.env.REACT_APP_GEMINI_API_KEY) {
  throw new Error("REACT_APP_GEMINI_API_KEY environment variable is not set.");
}

const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY });

const sceneSchema = {
  type: Type.OBJECT,
  properties: {
    scene_number: {
      type: Type.INTEGER,
      description: "The sequential number of the scene.",
    },
    description: {
      type: Type.STRING,
      description:
        "A detailed one-paragraph description of the action, setting, and characters in the scene.",
    },
    video_prompt: {
      type: Type.STRING,
      description:
        "A short, concise prompt for video generation describing the motion, camera movement, or animation style for this scene (e.g., 'slow motion flying', 'camera zoom in', 'gentle movement').",
    },
  },
  required: ["scene_number", "description", "video_prompt"],
};

export const generateSceneDescriptions = async (
  prompt: string,
  numScenes: number
): Promise<
  Omit<
    Scene,
    "imageUrl" | "isGeneratingImage" | "uploadStatus" | "leonardoImageId"
  >[]
> => {
  const userPrompt = `
    Based on the following story idea, generate a sequence of ${numScenes} distinct scenes.
    For each scene, provide:
    1. A detailed one-paragraph description covering the setting, characters, and action
    2. A short video_prompt describing the motion or camera movement for video generation
    
    The scenes should form a cohesive, logical narrative progression suitable for an image-to-video storyboard.

    Story Idea: "${prompt}"

    Please output a valid JSON array of scene objects, following the provided schema.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-pro",
      contents: userPrompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: sceneSchema,
        },
      },
    });

    const responseText = response.text?.trim();
    if (!responseText) {
      throw new Error("API returned an empty response.");
    }
    const scenes = JSON.parse(responseText);

    if (!Array.isArray(scenes)) {
      throw new Error("API did not return a valid array of scenes.");
    }

    return scenes.map((scene: any) => ({
      ...scene,
    }));
  } catch (error) {
    console.error("Error generating scene descriptions:", error);
    if (error instanceof SyntaxError) {
      throw new Error(
        "Failed to generate scene descriptions. The model returned invalid JSON."
      );
    }
    throw new Error(
      "Failed to generate scene descriptions. The model may have returned an invalid format."
    );
  }
};

const uploadToS3 = async (
  base64Image: string,
  imageName: string
): Promise<string> => {
  const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, "");
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const params = {
    Bucket: "tbo-assets",
    Key: `stories/${imageName}.png`,
    Body: bytes,
    ContentType: "image/png",
  };

  const result = await s3.upload(params).promise();
  return `https://d1wwvg89fh4xli.cloudfront.net/stories/${imageName}.png`;
};

export const uploadImageToS3 = async (
  base64Image: string,
  imageName: string
): Promise<string> => {
  const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, "");
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const params = {
    Bucket: "tbo-assets",
    Key: `stories/${imageName}.png`,
    Body: bytes,
    ContentType: "image/png",
  };

  await s3.upload(params).promise();
  return `https://d1wwvg89fh4xli.cloudfront.net/stories/${imageName}.png`;
};

export const uploadToLeonardo = async (
  imageUrl: string,
  imageName: string
): Promise<{ leonardo_id: string }> => {
  const response = await fetch(
    `${process.env.REACT_APP_VIDEO_API}/upload-leonardo-url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        imageUrl: imageUrl,
        imageName: imageName,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Leonardo upload failed: ${response.statusText}`);
  }

  const result = await response.json();
  return { leonardo_id: result.leonardo_id };
};

export const generateVideos = async (
  scenes: Array<{
    scene_number: number;
    video_prompt: string;
    leonardo_id: string;
  }>
): Promise<any> => {
  const payload = {
    script: {
      scenes: scenes.map((scene) => ({
        scene_number: scene.scene_number,
        prompt: scene.video_prompt,
        image_id: scene.leonardo_id,
      })),
    },
  };

  const response = await fetch(
    `${process.env.REACT_APP_VIDEO_API}/generate-leonardo-videos`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(`Video generation failed: ${response.statusText}`);
  }

  return await response.json();
};

export const checkVideoStatus = async (jobIds: string[]): Promise<any> => {
  const response = await fetch(
    `${process.env.REACT_APP_VIDEO_API}/check-video-status`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        job_ids: jobIds,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Status check failed: ${response.statusText}`);
  }

  return await response.json();
};

export const generateImageForScene = async (
  scene: Omit<Scene, "imageUrl" | "isGeneratingImage">,
  characterImage: CharacterImage | null,
  retryCount: number = 0
): Promise<string> => {
  // Enhanced prompt with Speedy reference and better character consistency
  const enhancedDescription = characterImage
    ? scene.description.replace(
        /\b(mascot|speedy)\b/gi,
        "Speedy from the reference image"
      )
    : scene.description;

  const textPrompt = `
    Create a highly detailed, cinematic, photorealistic image for this scene:
    
    "${enhancedDescription}"
    
    ${
      characterImage
        ? "IMPORTANT: Speedy MUST exactly match the appearance, clothing, and features of the character in the provided reference image. Maintain complete visual consistency."
        : ""
    }
    
    Style: dramatic lighting, cinematic quality, high resolution, suitable for storytelling.
  `;

  const parts: any[] = [];

  if (characterImage) {
    parts.push({
      inlineData: {
        data: characterImage.base64Data,
        mimeType: characterImage.mimeType,
      },
    });
  }

  parts.push({ text: textPrompt });

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-image",
      contents: { parts: parts },
      config: {
        responseModalities: [Modality.IMAGE],
      },
    });

    const candidate = response.candidates?.[0];

    if (!candidate) {
      const blockReason = response.promptFeedback?.blockReason;
      if (blockReason) {
        throw new Error(`Image blocked: ${blockReason}`);
      }
      throw new Error("No image generated");
    }

    if (candidate.finishReason && candidate.finishReason !== "STOP") {
      throw new Error(`Generation stopped: ${candidate.finishReason}`);
    }

    if (!candidate.content?.parts) {
      throw new Error("No image data in response");
    }

    for (const part of candidate.content.parts) {
      if (part.inlineData?.data) {
        const base64ImageBytes = part.inlineData.data;
        const mimeType = part.inlineData.mimeType || "image/png";
        const dataUrl = `data:${mimeType};base64,${base64ImageBytes}`;

        return dataUrl;
      }
    }

    throw new Error("No image data found");
  } catch (error) {
    console.error(`Image generation attempt ${retryCount + 1} failed:`, error);

    const errorMessage = error instanceof Error ? error.message : String(error);

    // Retry up to 2 times for transient failures
    if (retryCount < 2 && !errorMessage.includes("blocked")) {
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * (retryCount + 1))
      );
      return generateImageForScene(scene, characterImage, retryCount + 1);
    }

    throw new Error(errorMessage || "Image generation failed");
  }
};
